/**
 * Apply a DiffResult to a live workflow via the existing REST endpoints
 * (`POST /workflows/:id/nodes`, `PUT /workflows/:id/nodes/:nodeId`,
 * `POST /workflows/:id/nodes/bulk-delete`, edge routes, `PUT /workflows/:id`).
 *
 * Returns the set of `(sourceLine → newNodeId)` pairs the writeback step
 * needs to stamp the spec file with fresh `@id` comments.
 *
 * @module spec/sync
 */

import { fetchOrExit } from '../api-fetch.js';
import {
  reconcileLinearEdges,
  type DiffResult,
  type EmittedWorkflow,
  type LiveEdge,
  type LiveWorkflow,
  type SpecOp,
  type WorkflowFieldChanges,
} from '@chatsdet/canary-spec';

export type SyncContext = {
  apiUrl: string;
  token: string;
  workflowId: string;
};

export type SyncResult = {
  /** New nodes that were created — caller uses these to write `@id` back to the spec. */
  created: Array<{ sourceLine: number; nodeId: string }>;
};

export async function applyDiff(
  ctx: SyncContext,
  diff: DiffResult,
  emitted: EmittedWorkflow,
  live: LiveWorkflow,
): Promise<SyncResult> {
  // 1. Bulk-delete removed nodes (reconnect: true so adjacent edges heal).
  const deletes = diff.ops.filter((o): o is Extract<SpecOp, { kind: 'delete' }> => o.kind === 'delete');
  if (deletes.length > 0) {
    await postJson(ctx, `/workflows/${ctx.workflowId}/nodes/bulk-delete`, {
      nodeIds: deletes.map((o) => o.nodeId),
      reconnect: true,
    });
  }

  // 2. Create new nodes. We need final IDs back for both writeback and
  //    downstream edge reconciliation; final order is realized by edges.
  const creates = diff.ops.filter((o): o is Extract<SpecOp, { kind: 'create' }> => o.kind === 'create');
  const createdIds = new Map<number, string>(); // specIndex → new node UUID
  const writebackPairs: SyncResult['created'] = [];
  for (const op of creates) {
    const res = await postJson<{ ok?: boolean; node?: { id: string }; error?: string }>(
      ctx,
      `/workflows/${ctx.workflowId}/nodes`,
      {
        nodeType: op.nodeType,
        configJson: op.configJson,
      },
    );
    const created = res.node;
    if (!created) {
      throw new Error(
        `Failed to create node at specIndex ${op.specIndex}: ${res.error ?? JSON.stringify(res)}`,
      );
    }
    createdIds.set(op.specIndex, created.id);
    if (op.sourceLine !== undefined) {
      writebackPairs.push({ sourceLine: op.sourceLine, nodeId: created.id });
    }
  }

  // 3. Update existing nodes whose config changed. Reorders are handled by
  //    edge reconciliation below, not by the deprecated order_index column.
  const updates = diff.ops.filter((o): o is Extract<SpecOp, { kind: 'update' }> => o.kind === 'update');
  for (const op of updates) {
    if (!op.changes.configChanged) continue;
    await putJson(ctx, `/workflows/${ctx.workflowId}/nodes/${op.nodeId}`, {
      configJson: op.configJson,
    });
  }

  // 4. Edge reconciliation. Spec authors a single linear default chain; we
  //    re-derive the expected edges from final node order and DELETE+POST to
  //    converge. Non-default edges (condition then/else) are left alone.
  if (diff.ops.length > 0) {
    const finalNodeIds = computeFinalNodeOrder(diff, live, createdIds);
    const fresh = await getJson<{ workflow?: { edges?: LiveEdge[] } }>(
      ctx,
      `/workflows/${ctx.workflowId}`,
    );
    const liveEdges = fresh.workflow?.edges ?? live.edges ?? [];
    const { ops: edgeOps } = reconcileLinearEdges(finalNodeIds, liveEdges);
    for (const op of edgeOps) {
      if (op.kind === 'delete') {
        await deleteJson(ctx, `/workflows/${ctx.workflowId}/edges/${op.edgeId}`);
      } else {
        await postJson(ctx, `/workflows/${ctx.workflowId}/edges`, {
          sourceNodeId: op.sourceNodeId,
          targetNodeId: op.targetNodeId,
          edgeType: 'default',
        });
      }
    }
  }

  // 5. Workflow-level fields. Some fields require name → id resolution against
  //    org/property scope (tags, environments, sandboxTemplate); those land in
  //    a follow-up PR. Surface a clear error rather than silently dropping the
  //    edit, so the author isn't surprised by missing changes.
  rejectUnsupportedFieldEdits(diff.workflowFieldChanges);
  if (Object.keys(diff.workflowFieldChanges).length > 0) {
    const payload = await resolveFieldUpdatePayload(ctx, diff.workflowFieldChanges);
    await putJson(ctx, `/workflows/${ctx.workflowId}`, payload);
  }

  // 6. Optional api_sequence DSL upsert. The spec carries the full
  //    ApiSequenceConfig in a YAML sidecar referenced from any api_sequence
  //    node; here we POST it to `/api-sequences/save`, which upserts the
  //    workflow-scoped row keyed by workflowId. Skipping when the sidecar is
  //    absent leaves any existing live config untouched (no implicit clear).
  if (emitted.apiSequenceConfig) {
    await postJson(ctx, `/api-sequences/save`, {
      workflowId: ctx.workflowId,
      config: emitted.apiSequenceConfig,
    });
  }

  return { created: writebackPairs };
}

/**
 * Look up a teardown workflow by name in the org. Matches on (name, flowType
 * === 'teardown'); throws when missing so the author gets a clear "compile the
 * teardown spec first" message instead of a silent FK clear.
 */
async function resolveTeardownWorkflowId(
  ctx: SyncContext,
  specName: string,
): Promise<string> {
  const res = await getJson<{
    ok?: boolean;
    data?: Array<{ id: string; name: string; flowType: string }>;
  }>(
    ctx,
    `/workflows?search=${encodeURIComponent(specName)}&flowType=teardown&pageSize=50`,
  );
  const match = (res.data ?? []).find(
    (w) => w.name === specName && w.flowType === 'teardown',
  );
  if (!match) {
    throw new Error(
      `Teardown workflow "${specName}" not found (flowType=teardown). ` +
        `Compile the teardown spec first so the seed can link to it by name.`,
    );
  }
  return match.id;
}

/**
 * Map name-form workflow fields to their wire shape. Currently only teardown
 * and seedOutputs need lookup; tags/environments/sandboxTemplate are gated by
 * `rejectUnsupportedFieldEdits` above and never reach here.
 */
async function resolveFieldUpdatePayload(
  ctx: SyncContext,
  changes: WorkflowFieldChanges,
): Promise<Record<string, unknown>> {
  const payload = fieldUpdatePayload(changes);
  // teardownSpec carries the name; the API expects teardownWorkflowId.
  if ('teardownSpec' in payload) {
    const specName = payload.teardownSpec;
    delete payload.teardownSpec;
    if (specName === null) {
      payload.teardownWorkflowId = null;
    } else if (typeof specName === 'string') {
      payload.teardownWorkflowId = await resolveTeardownWorkflowId(ctx, specName);
    }
  }
  // teardownSnapshotVersion: 'latest' → null on the wire (DB convention).
  if ('teardownSnapshotVersion' in payload) {
    if (payload.teardownSnapshotVersion === 'latest') payload.teardownSnapshotVersion = null;
  }
  // seedOutputs: rename to `seedOutputsJson` and resolve propertyName → id.
  // The route's normalizeSeedOutputs accepts entries with propertyId set;
  // we ferry across credentialOutput/attributes verbatim per the diff layer.
  if ('seedOutputs' in payload) {
    const inputRows = payload.seedOutputs as
      | Array<{
          dataKey: string;
          label?: string;
          description?: string;
          propertyName?: string;
          credentialOutput?: unknown;
          attributes?: unknown;
        }>
      | undefined;
    delete payload.seedOutputs;
    if (inputRows) {
      const propIds = await resolvePropertyNameMap(
        ctx,
        Array.from(new Set(inputRows.map((r) => r.propertyName).filter((p): p is string => !!p))),
      );
      payload.seedOutputsJson = inputRows.map((r) => {
        const propertyId = r.propertyName ? propIds.get(r.propertyName) : undefined;
        if (r.propertyName && !propertyId) {
          throw new Error(
            `Declared output "${r.dataKey}": property "${r.propertyName}" not found in this org.`,
          );
        }
        return {
          dataKey: r.dataKey,
          name: r.dataKey,
          displayLabel: r.label ?? r.dataKey,
          ...(r.description ? { description: r.description } : {}),
          ...(propertyId ? { propertyId } : {}),
          ...(r.credentialOutput !== undefined ? { credentialOutput: r.credentialOutput } : {}),
          ...(r.attributes !== undefined ? { attributes: r.attributes } : {}),
        };
      });
    }
  }
  return payload;
}

/**
 * Look up property ids by name in a single round trip. Used for seed-output
 * declarations where the spec author writes `property: <name>` and sync needs
 * the propertyId for the wire payload.
 */
async function resolvePropertyNameMap(
  ctx: SyncContext,
  names: string[],
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  if (names.length === 0) return out;
  const res = await getJson<{
    ok?: boolean;
    data?: Array<{ id: string; name: string }>;
    properties?: Array<{ id: string; name: string }>;
  }>(ctx, `/org/properties`);
  const all = res.data ?? res.properties ?? [];
  for (const want of names) {
    const match = all.find((p) => p.name === want);
    if (match) out.set(want, match.id);
  }
  return out;
}

export class UnsupportedFieldEditError extends Error {
  constructor(fields: string[]) {
    super(
      `Editing ${fields.join(', ')} via a spec file is not supported yet. ` +
        `These fields require name → id resolution that lands in a follow-up. ` +
        `Edit them in the Canary UI for now; the rest of your changes will still apply.`,
    );
    this.name = 'UnsupportedFieldEditError';
  }
}

export function rejectUnsupportedFieldEdits(changes: WorkflowFieldChanges): void {
  const unsupported = (['tags', 'environmentConstraints', 'sandboxTemplate'] as const).filter(
    (k) => changes[k] !== undefined,
  );
  if (unsupported.length > 0) throw new UnsupportedFieldEditError(unsupported);
}

/**
 * Build the final ordered list of node UUIDs after applying the diff —
 * positions correspond to the spec's order, IDs resolved from either the live
 * workflow (for UPDATE) or the new-node map (for CREATE).
 */
function computeFinalNodeOrder(
  diff: DiffResult,
  live: LiveWorkflow,
  createdIds: Map<number, string>,
): string[] {
  const liveById = new Map(live.nodes.map((n) => [n.id, n] as const));
  const total = diff.ops.filter((o) => o.kind !== 'delete').length;
  const result: string[] = Array.from({ length: total });
  for (const op of diff.ops) {
    if (op.kind === 'create') {
      const id = createdIds.get(op.specIndex);
      if (!id) throw new Error(`Missing created node id for specIndex ${op.specIndex}`);
      result[op.specIndex] = id;
    } else if (op.kind === 'update') {
      if (!liveById.has(op.nodeId)) {
        throw new Error(`UPDATE references nodeId ${op.nodeId} not in live workflow`);
      }
      result[op.specIndex] = op.nodeId;
    }
  }
  return result;
}

function fieldUpdatePayload(changes: WorkflowFieldChanges): Record<string, unknown> {
  // PUT /workflows/:id accepts each field independently; pass through only
  // what changed. Scenario `skipReason` is spec-source-only — the API's
  // `normalizeScenarios` strips it, but stripping here keeps the wire payload
  // honest and the round-trip test deterministic.
  const payload: Record<string, unknown> = {};
  for (const k of Object.keys(changes) as (keyof WorkflowFieldChanges)[]) {
    if (k === 'scenariosJson' && Array.isArray(changes.scenariosJson)) {
      payload.scenariosJson = (changes.scenariosJson as Array<Record<string, unknown>>).map((s) => {
        const { skipReason: _skip, ...rest } = s;
        return rest;
      });
    } else {
      payload[k] = changes[k];
    }
  }
  return payload;
}

async function postJson<T = unknown>(
  ctx: SyncContext,
  path: string,
  body: unknown,
): Promise<T> {
  const res = await fetchOrExit(`${ctx.apiUrl}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${ctx.token}`,
      'Content-Type': 'application/json',
    },
    token: ctx.token,
    body: JSON.stringify(body),
  });
  return (await res.json()) as T;
}

async function putJson<T = unknown>(
  ctx: SyncContext,
  path: string,
  body: unknown,
): Promise<T> {
  const res = await fetchOrExit(`${ctx.apiUrl}${path}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${ctx.token}`,
      'Content-Type': 'application/json',
    },
    token: ctx.token,
    body: JSON.stringify(body),
  });
  return (await res.json()) as T;
}

async function getJson<T = unknown>(ctx: SyncContext, path: string): Promise<T> {
  const res = await fetchOrExit(`${ctx.apiUrl}${path}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${ctx.token}` },
    token: ctx.token,
  });
  return (await res.json()) as T;
}

async function deleteJson<T = unknown>(ctx: SyncContext, path: string): Promise<T> {
  const res = await fetchOrExit(`${ctx.apiUrl}${path}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${ctx.token}` },
    token: ctx.token,
  });
  return (await res.json()) as T;
}
